import * as THREE from 'three';
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js';
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
import { OutputPass } from 'three/addons/postprocessing/OutputPass.js';
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js';
import { GTAOPass } from 'three/addons/postprocessing/GTAOPass.js';
import { InputController } from '../core/InputController';
import { Loop } from '../core/Loop';
import { createRenderer, resizeRenderer } from '../core/Renderer';
import { Player } from '../entities/Player';
import { PLAYFIELD } from './constants';
import { BulletSystem } from '../systems/BulletSystem';
import { DebugTools, type DebugTuning } from '../systems/DebugTools';
import { EnemyRole, EnemySystem } from '../systems/EnemySystem';
import { EffectsSystem } from '../systems/EffectsSystem';
import { HomeFieldEnvironment } from '../systems/HomeFieldEnvironment';
import { Hud } from '../systems/Hud';
import { PlayerShotSystem } from '../systems/PlayerShotSystem';
import { StageDirector } from '../systems/StageDirector';
import { AudioSystem } from '../systems/AudioSystem';
import { GameFeel } from '../systems/GameFeel';
import { BossSystem } from '../systems/BossSystem';
import { PropField } from '../systems/PropField';
import { PickupSystem } from '../systems/PickupSystem';
import { MidbossSystem } from '../systems/MidbossSystem';
import { TitleScreen } from '../systems/TitleScreen';
import { HomingSystem } from '../systems/HomingSystem';
import { AllySystem } from '../systems/AllySystem';
import { SwarmMotherSystem } from '../systems/SwarmMotherSystem';
import { EnemyMissileSystem } from '../systems/EnemyMissileSystem';
import { createSeededRandom } from '../utils/random';
import { ARMOR_RESPONSE } from './combat';
import { getDifficulty, loadDifficulty } from './difficulty';

/**
 * Damage a single player round deals to a boss component.
 * BATTERY NINE is 12,000 HP; at roughly 40 rounds/sec landing this clears in
 * ~50 s, which sits inside the bible's 45-90 s target for a stage boss.
 */
const BOSS_SHOT_DAMAGE = 5;

/**
 * Damage one player round deals to scenery. Structures are meant to fall in a
 * sustained burst, not survive a strafing run — a barn at 220 HP goes down in
 * roughly a second of held fire.
 */
const PROP_SHOT_DAMAGE = 14;

/**
 * Ambient occlusion on/off.
 *
 * AO is the single biggest visual upgrade available for a top-down game —
 * without contact shading, structures float on their fields. It is also a
 * full-screen per-pixel pass, and this project's hard constraint is 60 fps on
 * an M1.
 *
 * Kept as one switch rather than buried in the composer so the cost can be
 * A/B'd in seconds: flip it, `npm run build`, play. Verification has to happen
 * on real hardware — the headless harness rasterises in software and
 * over-states the cost of anything screen-space.
 */
const AMBIENT_OCCLUSION = false;

export class Game {
  private readonly renderer: THREE.WebGLRenderer;
  private readonly composer: EffectComposer;
  /** Ambient occlusion. Render-only; see createScene for why it matters. */
  private readonly gtao: GTAOPass;
  private readonly environmentTarget: THREE.WebGLRenderTarget;
  private readonly scene = new THREE.Scene();
  private readonly camera = new THREE.PerspectiveCamera(44, 1, 0.1, 160);
  private readonly input: InputController;
  private readonly player = new Player();
  private readonly bullets = new BulletSystem();
  private readonly playerShots = new PlayerShotSystem();
  private readonly enemies = new EnemySystem();
  private readonly effects = new EffectsSystem();
  private readonly environment = new HomeFieldEnvironment();
  private readonly stageDirector = new StageDirector();
  private readonly audio = new AudioSystem();
  private readonly feel = new GameFeel();
  private readonly boss = new BossSystem();
  /**
   * THE STAGE 1 BOSS. Replaces BATTERY NINE, which was a captured air-defence
   * battery — and a captured air-defence battery is a chunk of metal with
   * guns no matter how well it is modelled. See docs/BOSS-SWARM-MOTHER.md.
   */
  private readonly mother = new SwarmMotherSystem();
  private readonly props = new PropField();
  private readonly pickups = new PickupSystem();
  private readonly midboss = new MidbossSystem();
  private readonly homing = new HomingSystem();
  private readonly allies = new AllySystem();
  private readonly enemyMissiles = new EnemyMissileSystem();
  private readonly title = new TitleScreen();
  private paused = false;
  private readonly extendsAwarded = new Set<number>();
  private firstMissileDropped = false;
  /** Held-trigger by default; toggled with V. */
  private autoFire = false;
  private readonly hud = new Hud();
  private readonly loop = new Loop(
    (delta, elapsed) => this.update(delta, elapsed),
    (alpha) => this.render(alpha),
  );
  private readonly tuning: DebugTuning & {
    cruiseSpeed: number;
    trimSpeed: number;
    scrollSpeed: number;
  } = {
    cameraLag: 0,
    exposure: 0.94,
    maxDpr: 1.75,
    cruiseSpeed: 21,
    trimSpeed: 8.4,
    scrollSpeed: 12,
  };
  private readonly debugTools: DebugTools;
  private frame = 0;
  private runElapsed = 0;
  private score = 0;
  private kills = 0;
  /** Medals collected. End-of-stage bonus multiplies these by bombs held. */
  private medals = 0;
  private midbossCleared = false;
  /** Eased scroll speed — driven to 0 during boss encounters. */
  private scrollNow = 12;
  /** Remaining seconds of the boss's sweeping beam attack. */
  private bossBeamTimer = 0;
  private failed = false;
  private complete = false;
  private pausedForScreenshot = false;
  private reducedMotion = false;
  private rng = createSeededRandom(2090);

  constructor(private readonly canvas: HTMLCanvasElement) {
    // Three's loader cache is OFF by default, so two systems asking for the
    // same file fetch it twice and decode it twice. Every model here is
    // procedurally forged and immutable, so caching is always correct.
    THREE.Cache.enabled = true;
    this.renderer = createRenderer(canvas);
    this.renderer.info.autoReset = false;
    this.renderer.toneMappingExposure = this.tuning.exposure;
    const roomEnvironment = new RoomEnvironment();
    const pmrem = new THREE.PMREMGenerator(this.renderer);
    pmrem.compileEquirectangularShader();
    this.environmentTarget = pmrem.fromScene(roomEnvironment, 0.04);
    this.scene.environment = this.environmentTarget.texture;
    this.scene.environmentIntensity = 0.46;
    roomEnvironment.dispose();
    pmrem.dispose();

    this.composer = new EffectComposer(this.renderer);
    this.composer.addPass(new RenderPass(this.scene, this.camera));

    // GROUND-TRUTH AMBIENT OCCLUSION.
    //
    // The single highest-value pass for a top-down game. Without contact
    // shading every structure floats: a barn and its field are lit almost
    // identically from above, so the eye gets no cue that one is sitting on
    // the other. AO puts a dark seam where geometry meets geometry, and that
    // seam is most of what separates "a photograph of farmland" from "coloured
    // shapes on a plane".
    //
    // Render-only. It never touches the simulation, needs no network, and
    // cannot affect determinism — so it costs frame time and nothing else.
    this.gtao = new GTAOPass(this.scene, this.camera, 1, 1);
    this.gtao.output = GTAOPass.OUTPUT.Default;
    // Tuned for the stage's scale: the interesting occlusion is at building
    // scale (a few world units), not at terrain scale.
    this.gtao.updateGtaoMaterial({
      radius: 0.9,
      distanceExponent: 1.4,
      thickness: 1.4,
      scale: 1.1,
      // 12 samples measured 5x slower than no AO at all in the headless
      // harness. Software rasterisation over-penalises per-pixel work, but a
      // regression that large is not something to ship on an assumption, so
      // this runs at the cheapest sample count that still reads.
      samples: 6,
    });
    this.gtao.enabled = AMBIENT_OCCLUSION;
    this.composer.addPass(this.gtao);

    const bloom = new UnrealBloomPass(
      new THREE.Vector2(1, 1),
      0.26,
      0.28,
      1.28,
    );
    this.composer.addPass(bloom);
    this.composer.addPass(new OutputPass());
    this.camera.position.set(0, -37, 43);
    this.camera.lookAt(0, 3, -0.6);

    const stick = this.getElement('#touch-stick');
    const knob = this.getElement('#touch-knob');
    const focusButton = this.getElement('#focus-button');
    this.input = new InputController(stick, knob, focusButton);
    this.debugTools = new DebugTools(this.tuning, () => {
      this.renderer.toneMappingExposure = this.tuning.exposure;
      if (resizeRenderer(this.renderer, this.camera, this.tuning.maxDpr)) {
        this.resizeComposer();
      }
    });
    // A Kamikaze committing is the single most important tell in Act I —
    // announce it so the player can triage under pressure.
    this.enemies.onLock = (x, y) => {
      this.audio.play('drone-picket-lock', 0.55, x / PLAYFIELD.halfWidth);
      this.feel.hitSpark(x, y, 1);
    };
    // THE SWARM MOTHER. Every cue is a MASS of small sources rather than one
    // large one — that contrast against Battery Nine's machine sounds is most
    // of what tells the player they are fighting something else entirely.
    this.mother.onEvent = (kind, x, y) => {
      if (kind === 'reveal') {
        this.feel.addHitstop(220);
        this.feel.addShake(0.5);
        this.audio.play('mother-morph', 1.0);
        this.audio.say('rios-boss', { priority: 1 });
      } else if (kind === 'morph') {
        // Ten thousand bodies changing direction together.
        this.audio.play('mother-morph', 0.85);
        this.feel.addShake(0.22);
      } else if (kind === 'iris-open') {
        // The tell. This is the only moment her core can be hurt, so it gets
        // the biggest, wettest sound in the bank and a shake to match.
        this.audio.play('mother-birth', 1.0);
        this.feel.addShake(0.3);
        this.feel.addHitstop(90);
      } else if (kind === 'iris-shut') {
        this.audio.play('mother-iris-shut', 0.8);
      } else if (kind === 'core-hit') {
        this.audio.play('mother-core-hit', 0.7, x / PLAYFIELD.halfWidth);
        this.feel.hitSpark(x, y, 1);
      } else if (kind === 'death') {
        this.feel.addHitstop(320);
        this.feel.addShake(0.9);
        this.audio.play('mother-death', 1.0);
        this.audio.play('explosion-large', 1.0);
      }
    };

    this.mother.onFire = (kind, x) => {
      const pan = x / PLAYFIELD.halfWidth;
      if (kind === 'curtain') this.audio.play('mother-curtain', 0.5, pan);
      else this.audio.play('mother-lash', 0.6, pan);
    };

    // BATTERY NINE theatre. Each beat gets its own weight so the fight reads
    // as an escalating event rather than a health bar draining.
    this.boss.onEvent = (kind, x, y) => {
      if (kind === 'reveal') {
        // The eyeball moment: armour peels, the core lights. Hold the frame.
        this.feel.addHitstop(220);
        this.feel.addShake(0.5);
        this.feel.kill(x, y, 'structure');
        this.audio.play('explosion-large', 1.0);
        this.audio.say('rios-boss', { priority: 1 });
      } else if (kind === 'phase') {
        this.feel.addShake(0.34);
        this.feel.addHitstop(120);
        this.audio.play('explosion-medium', 0.9);
      } else if (kind === 'salvo') {
        // Homing salvo out of the launch drums. Fans wide, then turns in.
        for (let i = 0; i < 7; i += 1) {
          const side = i % 2 === 0 ? -1 : 1;
          this.enemyMissiles.spawn(x + side * 2.4, y - 1.4, side, Math.floor(i / 2));
        }
        this.audio.play('player-bomb-deploy', 0.55);
        this.feel.addShake(0.18);
      } else if (kind === 'drones') {
        // The bays open and the battery births escorts. Kill the source or
        // drown — the same lesson the DRAYMAN carrier teaches.
        for (let i = 0; i < 6; i += 1) {
          this.enemies.spawn(0 as never, x - 6 + i * 2.4, y - 2.5);
        }
        this.audio.play('drone-tick-swarm', 0.7);
        this.feel.addShake(0.12);
      } else if (kind === 'beam') {
        // Sweeping laser out of the Lattice-grown emitter lenses.
        this.bossBeamTimer = 1.9;
        this.audio.play('player-arclight', 0.9);
        this.feel.addShake(0.2);
      } else if (kind === 'pipe') {
        this.feel.kill(x, y, 'armored');
        this.audio.play('explosion-medium', 0.75);
        this.score += 400;
      } else {
        this.feel.kill(x, y, 'armored');
        this.audio.play('explosion-large', 0.85);
      }
    };
    this.midboss.onEvent = (kind, x, y) => {
      if (kind === 'enrage') {
        // The survivor takes over both roles. Announce it — the fight just
        // changed shape rather than getting easier.
        this.feel.addShake(0.4);
        this.feel.addHitstop(140);
        this.feel.kill(x, y, 'armored');
        this.audio.play('explosion-large', 0.9);
      } else if (kind === 'death') {
        this.feel.addHitstop(220);
        this.feel.kill(x, y, 'structure');
        this.audio.play('explosion-large', 1.0);
        this.score += 25000;
      } else {
        this.feel.kill(x, y, 'armored');
        this.audio.play('explosion-medium', 0.85);
        this.score += 3000;
      }
    };
    this.allies.onEvent = (kind, x, y) => {
      if (kind === 'arrive') {
        // Every allied hull is a unit confirmed clean — its arrival is a beat.
        // Overwatch coordinates support — that is the controller's actual job,
        // and saying so is what makes the role legible to the player.
        this.audio.say('porch-ally-inbound', { priority: 1 });
        window.setTimeout(() => this.audio.say('rios-ally-thanks', { priority: 2 }), 2600);
        window.setTimeout(() => this.audio.say('porch-ally-guns', { priority: 2 }), 5200);
        this.feel.addShake(0.12);
      } else if (kind === 'salvo') {
        this.feel.addShake(0.22);
        this.audio.play('player-bomb-deploy', 0.5, x / PLAYFIELD.halfWidth);
      } else if (kind === 'resupply') {
        // A crate of ordnance, put up by the friendly. Two pods and a missile
        // rack — enough to meaningfully change the player's loadout.
        this.pickups.spawn('missile', x - 1.6, y);
        this.pickups.spawn('pod', x + 1.6, y);
        this.pickups.spawn('shield', x, y + 1.8);
        this.audio.say('porch-ally-resupply', { priority: 1 });
        this.feel.addShake(0.1);
      } else if (kind === 'lost') {
        this.feel.kill(x, y, 'structure');
        this.feel.addShake(0.45);
        this.audio.play('explosion-large', 0.95);
        // Explicitly absolves the player — an ally loss they could not have
        // prevented must never read as a mistake they made.
        this.audio.say('porch-ally-lost', { priority: 1 });
      }
    };
    loadDifficulty();
    this.createScene();
    this.resetRun();
    resizeRenderer(this.renderer, this.camera, this.tuning.maxDpr);
    this.resizeComposer();
    this.title.whenStarted(() => {
      void this.audio.unlock();
      this.resetRun();
    });
    this.installTestHooks();
    this.publishDiagnostics();
  }

  start(): void {
    this.loop.start();
  }

  dispose(): void {
    this.loop.stop();
    this.input.dispose();
    this.debugTools.dispose();
    this.player.dispose();
    this.bullets.dispose();
    this.playerShots.dispose();
    this.enemies.dispose();
    this.effects.dispose();
    this.feel.dispose();
    this.boss.dispose();
    this.props.dispose();
    this.pickups.dispose();
    this.midboss.dispose();
    this.homing.dispose();
    this.allies.dispose();
    this.enemyMissiles.dispose();
    this.environment.dispose();
    this.audio.dispose();
    this.title.dispose();
    this.composer.dispose();
    this.environmentTarget.dispose();
    this.renderer.dispose();
    window.__THREE_GAME_DIAGNOSTICS__ = undefined;
    window.__THREE_GAME_TEST_HOOKS__ = undefined;
  }

  private update(delta: number, _elapsed: number): void {
    this.frame += 1;
    if (resizeRenderer(this.renderer, this.camera, this.tuning.maxDpr)) {
      this.resizeComposer();
    }
    if (this.pausedForScreenshot) {
      this.publishDiagnostics();
      return;
    }
    if (this.input.consumePause() && !this.failed && !this.complete) {
      this.paused = !this.paused;
      if (this.paused) this.audio.stopMusic(0.35);
    }
    if (this.paused) {
      this.hud.update(this.hudState());
      this.publishDiagnostics();
      return;
    }
    const pauseEl = document.querySelector('#pause-overlay');
    pauseEl?.classList.toggle('hidden', !this.paused);
    if (this.title.visible || this.title.introVisible) {
      // Title held. Render only — the stage should not advance behind it.
      this.hud.update(this.hudState());
      this.publishDiagnostics();
      return;
    }
    if (this.input.consumeRestart() && (this.failed || this.complete)) this.resetRun();
    if (this.failed || this.complete) {
      this.hud.update(this.hudState());
      this.publishDiagnostics();
      return;
    }

    this.runElapsed += delta;

    // Stage 1 opens quiet — Virginia farmland at 06:40. Ambience starts as soon
    // as the context is unlocked and the bank has decoded; both are no-ops until then.
    if (this.audio.hasSamples) {
      this.audio.loop('amb-farmland', 0.22);
      this.audio.loop('amb-wind-high', 0.16);
    }

    // Music as a pure function of game state. playMusic() is idempotent, so
    // calling it every frame simply holds the current track.
    if (this.audio.hasMusic) {
      if (this.mother.active && !this.mother.defeated) {
        // The Mother stops imitating anything once her body is mostly gone,
        // so the granular swarm theme gives way to something colder for the
        // last stretch. Threshold matches the LATTICE/COLLAPSE end of the arc.
        this.audio.playMusic(
          this.mother.integrity < 0.3 ? 'boss-swarm-mother-collapse' : 'boss-swarm-mother',
          1.2,
        );
      } else if (this.boss.active) this.audio.playMusic('boss-battery-nine', 1.2);
      else if (this.bullets.count > 190) this.audio.playMusic('home-field-surge', 2.0);
      else this.audio.playMusic('home-field', 2.4);
    }

    // Stage 1 voice beats, keyed to stage time. `once: true` makes these
    // idempotent so they survive being called every frame.
    if (this.audio.hasVoice) {
      const t = this.runElapsed;
      if (t > 1.0 && t < 30) this.audio.say('porch-launch', { once: true });
      if (t > 6.5 && t < 30) this.audio.say('rios-launch', { once: true });
      if (t > 13 && t < 40) this.audio.say('wight-brief', { once: true });
      if (t > 24 && t < 60) this.audio.say('porch-corridor', { once: true });
      if (t > 73 && t < 110) this.audio.say('porch-bridge', { once: true });
      // ECHO crosses at 68% of the 170 s pre-boss window (bible 03 §1.2).
      if (t > 116 && t < 124) {
        this.audio.say('echo-mayday', { once: true, priority: 0 });
        this.audio.say('rios-echo', { once: true, priority: 1 });
      }
      if (t > 124 && t < 140) this.audio.say('porch-echo', { once: true });
      if (t > 140 && t < 175) this.audio.say('porch-base', { once: true });
    }

    // ARENA LOCK. The scroll eases to a stop when a boss commits, and holds.
    // The world going still is the clearest possible statement that the stage
    // is over and the encounter has begun.
    const arenaLocked = this.mother.active || this.boss.active || this.midboss.active;
    const targetScroll = arenaLocked ? 0 : this.tuning.scrollSpeed;
    this.scrollNow += (targetScroll - this.scrollNow) * Math.min(1, delta * 1.6);
    const scroll = this.reducedMotion ? 0 : this.scrollNow;
    this.environment.update(this.reducedMotion ? 0 : delta, scroll);
    this.props.update(this.reducedMotion ? 0 : delta, scroll);
    this.player.update(delta, this.input, this.tuning, PLAYFIELD);

    if (this.input.consumeAutoFireToggle()) this.autoFire = !this.autoFire;
    const wantsFire = this.autoFire || this.input.isFireHeld();
    if (wantsFire && this.player.fireCooldown <= 0) {
      // Power-tiered fire. The screen visibly fills with more of the player's
      // own ordnance as pods are collected — the escalation they asked for.
      this.playerShots.fire(
        this.player.group.position.x,
        this.player.group.position.y + 1.1,
        this.player.weapon,
        this.player.power,
      );
      this.player.fireCooldown = 0.105;
      // Rate-limited inside AudioSystem so 9.5 shots/sec does not turn to mud.
      this.audio.play('player-hailstorm', 0.16);
      // Sparks off both gun ports. Cheap and short — at 9.5/sec anything
      // longer becomes a continuous glow and stops reading as single shots.
      const px = this.player.group.position.x;
      const py = this.player.group.position.y;
      this.feel.muzzleFlash(px - 0.42, py + 1.1);
      this.feel.muzzleFlash(px + 0.42, py + 1.1);
    }

    // Missiles — the special weapon. Fires a homing salvo, capped so it
    // supplements the main gun rather than replacing it.
    if (this.input.consumeSpecial() && this.player.missiles > 0) {
      const px = this.player.group.position.x;
      const py = this.player.group.position.y;
      for (let i = 0; i < 2 + this.player.missiles; i += 1) {
        const a = Math.PI / 2 + (i - (1 + this.player.missiles) / 2) * 0.22;
        this.playerShots.fire(px + Math.cos(a) * 0.6, py + 1.0, 'ARCLIGHT', 3);
      }
      this.player.missiles -= 1;
      this.audio.play('player-arclight', 0.8);
      this.feel.addShake(0.14);
    }

    if (this.input.consumeBomb() && this.player.bombs > 0) {
      const cleared = this.bullets.clear() + this.enemyMissiles.clear();
      this.player.bombs -= 1;
      this.score += cleared * 5;
      // The bomb must actually KILL. Clearing bullets alone made it read as a
      // light show — 900 damage in an 11 u radius wipes anything light and
      // badly hurts armour, which is what "screen clear" should mean.
      const blastKills = this.enemies.damageArea(
        this.player.group.position.x,
        this.player.group.position.y,
        11,
        900,
        (kx, ky) => {
          this.feel.kill(kx, ky, 'light');
          this.audio.play('drone-pop', 0.4, kx / PLAYFIELD.halfWidth);
        },
      );
      this.kills += blastKills.kills;
      this.score += blastKills.score;
      if (this.boss.active) this.boss.damage(this.player.group.position.x, this.boss.group.position.y, 700);
      if (this.midboss.active) this.midboss.damage(this.player.group.position.x, 700);
      this.effects.spawnBomb(this.player.group.position.x, this.player.group.position.y);
      this.feel.addShake(0.55);
      this.audio.play('player-bomb-deploy', 0.9);
      // Ordnance levels the environment inside its radius. A bomb that clears
      // bullets but leaves a farmyard untouched reads as a UI effect, not a bomb.
      const blast = this.props.damageArea(
        this.player.group.position.x,
        this.player.group.position.y,
        11,
        999,
      );
      for (const h of blast) {
        if (!h.destroyed) continue;
        this.feel.kill(h.x, h.y, h.tier);
        this.score += h.score;
      }
      if (blast.some((h) => h.destroyed)) {
        this.audio.play('explosion-large', 0.8);
        this.audio.say('rios-friendly-kill', { priority: 2, once: true });
      }
      this.audio.say('rios-bomb', { priority: 2 });
    }

    const playerX = this.player.group.position.x;
    const playerY = this.player.group.position.y + 0.35;
    this.stageDirector.update(
      this.runElapsed,
      {
        allies: this.allies,
        enemies: this.enemies,
        bullets: this.bullets,
        playerX,
        playerY,
      },
      this.environment,
    );

    // TWO VEHICLES midboss gate at 140 s (bible 03 §1.2 beat sheet).
    if (!this.midboss.active && !this.midbossCleared && this.runElapsed >= 140) {
      this.midboss.spawn();
      this.audio.play('explosion-medium', 0.6);
      // Tells the player HOW to fight it — turrets before hulls.
      this.audio.say('porch-midboss', { priority: 1, once: true });
    }
    if (this.midboss.active) {
      this.midboss.update(delta, playerX, playerY, this.bullets);
      const hits = this.playerShots.collideAll(
        this.midboss.group.position.x,
        this.midboss.group.position.y,
        8.0,
      );
      if (hits > 0) {
        const res = this.midboss.damage(playerX, hits * BOSS_SHOT_DAMAGE);
        if (res.hit && !res.destroyed) this.feel.hitSpark(playerX, this.midboss.group.position.y - 1.2, 0.6);
      }
      if (!this.midboss.active && this.midboss.defeated) this.midbossCleared = true;
    }

    // BATTERY NINE gate at 170 s (bible 03 §0.3).
    if (!this.mother.active && !this.complete && this.stageDirector.shouldSpawnBoss(this.runElapsed)) {
      this.mother.spawn();
      this.audio.say('rios-boss', { priority: 1 });
      this.audio.play('explosion-large', 0.5);
    }
    // THE SWARM MOTHER. Damage is SPATIAL, not numeric — every round is
    // resolved at its own position, so the drones it kills are the drones it
    // actually flew into and the gap that leaves is the gap the player cut.
    // Collapsing this into one collideCircle against a body centre, the way
    // the old boss worked, would put every hole in the same place regardless
    // of aim and destroy the entire premise.
    if (this.mother.active && !this.mother.defeated) {
      this.mother.update(delta, playerX, playerY, this.bullets);
      this.playerShots.forEachActive((sx, sy, index) => {
        const res = this.mother.damage(sx, sy, BOSS_SHOT_DAMAGE);
        if (res.dronesKilled > 0) {
          this.playerShots.consume(index);
          this.score += res.dronesKilled * 25;
          this.feel.hitSpark(sx, sy, 0.5);
        } else if (res.coreHit) {
          this.playerShots.consume(index);
          this.score += 150;
          this.feel.hitSpark(sx, sy, 1);
        }
      });
      if (this.mother.defeated && !this.complete) {
        this.complete = true;
      }
    }

    if (this.boss.active) {
      this.boss.update(delta, playerX, playerY, this.bullets);

      // Sweeping beam. Emitted as a dense line of fast rounds so it stays in
      // the normal bullet pipeline — no special-case collision, and its gap
      // is still provable: be somewhere the sweep is not.
      if (this.bossBeamTimer > 0) {
        this.bossBeamTimer -= delta;
        const t = 1 - this.bossBeamTimer / 1.9;
        const bx = this.boss.group.position.x;
        const by = this.boss.group.position.y;
        const angle = -Math.PI / 2 - 0.7 + t * 1.4;
        for (let i = 0; i < 14; i += 1) {
          const d = 2 + i * 1.5;
          this.bullets.spawn(
            bx + Math.cos(angle) * d,
            by + Math.sin(angle) * d,
            Math.cos(angle) * 26,
            Math.sin(angle) * 26,
            0.26,
            1.2,
          );
        }
      }
      // Player fire against boss components. Each destructible changes what
      // the boss can DO, not just how it looks.
      // The arena is a target too — breaking a pipe run opens a crossing lane.
      if (this.frame % 3 === 0) this.boss.damageArena(playerX, playerY + 5, 30);
      const hits = this.playerShots.collideAll(
        this.boss.group.position.x,
        this.boss.group.position.y,
        9.0,
      );
      if (hits > 0) {
        const weak = this.boss.heartVulnerable;
        const res = this.boss.damage(playerX, this.boss.group.position.y, hits * BOSS_SHOT_DAMAGE);
        if (res.hit) {
          // Hot white sparks inside the vulnerability window, dull outside it.
          // The player learns the rhythm from the feedback, not from a manual.
          this.feel.hitSpark(playerX, this.boss.group.position.y - 1.4, weak ? 1 : 0.35);
          if (weak) {
            this.feel.addShake(0.06);
            if (this.frame % 10 === 0) this.hud.announceCrossfire(0, 0);
          }
        }
        if (res.destroyed) {
          this.feel.kill(this.boss.group.position.x, this.boss.group.position.y, 'armored');
          this.audio.play('explosion-medium', 0.85);
          this.score += 5000;
        }
      }
      if (this.boss.defeated && !this.complete) {
        // Staged destruction. Ten anchors across 4 s, walking from the broken
        // drum out through the pipe anchors and back into the core.
        for (const a of this.boss.deathAnchors()) {
          window.setTimeout(() => {
            this.feel.kill(a.x, a.y, 'structure');
            this.feel.addShake(0.4);
          }, a.delay * 1000);
        }
        this.feel.addHitstop(320);
        this.audio.play('explosion-large', 1.0);
        window.setTimeout(() => this.audio.play('explosion-large', 0.9), 1400);
        window.setTimeout(() => this.audio.play('explosion-large', 1.0), 3100);
        this.audio.say('rios-clear', { priority: 1, once: true });
        this.complete = true;
        this.score += 50000;
      }
    }

    this.enemies.update(delta, playerX, playerY, this.bullets);
    const picked = this.pickups.update(delta, playerX, playerY, 1.6, 3.5);
    for (const p of picked) {
      if (p.kind === 'pod') {
        // Same colour levels you up; a different colour swaps and costs a tier.
        // That is what makes the drifting colour a decision rather than a grab.
        const outcome = this.player.applyPod(p.weapon);
        this.audio.play('pickup-pod', outcome === 'level' ? 1.0 : 0.75);
        this.score += outcome === 'level' ? 800 : 400;
      } else if (p.kind === 'missile') {
        this.homing.grant();
        this.audio.play('pickup-pod', 1.0);
        this.audio.say('rios-missiles-hot', { priority: 2 });
        this.feel.addShake(0.18);
        this.score += 900;
      } else if (p.kind === 'shield') {
        // Stacks to 2 so a good run can bank a buffer, but never more —
        // the one-hit-death tension has to survive.
        this.player.shield = Math.min(2, this.player.shield + 1);
        this.audio.play('pickup-pod', 0.85);
        this.score += 600;
      } else if (p.kind === 'extend') {
        this.player.lives = Math.min(6, this.player.lives + 1);
        this.audio.play('pickup-pod', 1.0);
        this.feel.addShake(0.2);
        this.score += 2000;
      } else if (p.kind === 'bomb') {
        this.player.bombs = Math.min(5, this.player.bombs + 1);
        this.player.missiles = Math.min(4, this.player.missiles + 1);
        this.audio.play('pickup-pod', 0.7);
        this.score += 300;
      } else {
        this.medals += 1;
        this.audio.play('pickup-medal', 0.6);
        this.score += 100 + this.medals * 10;
      }
      this.feel.hitSpark(p.x, p.y, 1);
    }

    // Homing salvo — fires itself while the player keeps flying.
    this.homing.update(
      delta, playerX, playerY,
      this.enemies.activeCount, this.enemies.activeFlags,
      this.enemies.posX, this.enemies.posY,
      (index, hx, hy) => {
        const res = this.enemies.damageIndex(index, 26);
        this.feel.hitSpark(hx, hy, 0.95);
        if (res.killed) {
          this.kills += 1;
          this.score += res.score;
          this.feel.kill(hx, hy, 'light');
          this.audio.play('drone-pop', 0.45, hx / PLAYFIELD.halfWidth);
        }
      },
    );

    // Allied fire. Ally kills pay the player 25% — enough to feel good,
    // never enough to farm.
    this.allies.update(
      delta,
      this.enemies.activeCount, this.enemies.activeFlags,
      this.enemies.posX, this.enemies.posY,
      (index, hx, hy) => {
        // Missiles carry the salvo's punch; tracers are suppression.
        const res = this.enemies.damageIndex(index, this.allies.missileDamage);
        this.feel.hitSpark(hx, hy, 0.95);
        if (res.killed) {
          this.kills += 1;
          this.score += Math.round(res.score * 0.25);
          this.feel.kill(hx, hy, 'armored');
          this.audio.play('drone-pop', 0.5, hx / PLAYFIELD.halfWidth);
        }
      },
    );

    this.playerShots.update(delta);
    this.bullets.update(delta);
    const result = this.enemies.resolvePlayerShots(
      this.playerShots,
      (x, y, destroyed, _role, effectiveness = 0.5) => {
        this.effects.spawnImpact(x, y, destroyed);
        if (destroyed) {
          this.feel.kill(x, y, 'light');
          // Drop table. Pods are rare enough that grabbing one on the right
          // colour is a genuine event; medals keep the chase constant.
          const roll = this.rng();
          // Guaranteed early missile pod — the player should feel the
          // aircraft fight alongside them inside the first minute.
          if (!this.firstMissileDropped && this.runElapsed > 14) {
            this.firstMissileDropped = true;
            this.pickups.spawn('missile', x, y);
          }
          else if (roll < 0.028) this.pickups.spawn('missile', x, y);
          else if (roll < 0.073) this.pickups.spawn('pod', x, y);
          else if (roll < 0.072) this.pickups.spawn('shield', x, y);
          else if (roll < 0.095) this.pickups.spawn('bomb', x, y);
          else if (roll < 0.101) this.pickups.spawn('extend', x, y);
          else if (roll < 0.44) this.pickups.spawn('medal', x, y);
          // Pan by screen x so a swarm on the left reads on the left.
          this.audio.play('drone-pop', 0.5, x / PLAYFIELD.halfWidth);
        } else {
          // Survived the hit — sparks tell the player their weapon is
          // connecting but not cutting through. This is the readout that
          // makes the armor matchup table legible without a manual.
          // Hot white sparks = this weapon is cutting through. Dull orange =
          // wrong tool. The player learns the matchup without a manual.
          this.feel.hitSpark(x, y, effectiveness);
        }
      },
      this.player.weapon,
    );
    this.kills += result.kills;
    this.score += result.score;

    // Score extends at 150,000 and 400,000 — the thresholds the arcade
    // original itself used (visible on its attract screen).
    for (const threshold of [150000, 400000]) {
      if (this.score >= threshold && !this.extendsAwarded.has(threshold)) {
        this.extendsAwarded.add(threshold);
        this.player.lives = Math.min(6, this.player.lives + 1);
        this.audio.play('pickup-pod', 1.0);
        this.feel.addShake(0.25);
      }
    }

    // Cannon fire strafes the ground. Resolved against REAL shot positions —
    // the previous version sampled one fixed point ahead of the player every
    // fourth frame, which meant a 220 HP barn needed nine hits inside a 1.6 u
    // window and was effectively indestructible.
    this.enemyMissiles.update(delta, playerX, playerY);

    // Shooting missiles down. This is what turns an incoming salvo from a
    // dodging problem into a decision — break lock, or destroy it.
    this.playerShots.forEachActive((sx, sy, index) => {
      const icept = this.enemyMissiles.intercept(sx, sy, PROP_SHOT_DAMAGE);
      if (icept.hit) {
        this.playerShots.consume(index);
        if (icept.destroyed) {
          this.feel.kill(icept.x, icept.y, 'light');
          this.feel.addShake(0.1);
          this.audio.play('drone-pop', 0.55, icept.x / PLAYFIELD.halfWidth);
          this.score += 250;
        } else {
          this.feel.hitSpark(icept.x, icept.y, 0.9);
        }
        return;
      }
      const hits = this.props.damageArea(sx, sy, 0.5, PROP_SHOT_DAMAGE);
      if (hits.length === 0) return;
      // The round is spent on the structure it hit.
      this.playerShots.consume(index);
      for (const h of hits) {
        if (h.destroyed) {
          this.feel.kill(h.x, h.y, h.tier);
          this.audio.play(h.tier === 'light' ? 'explosion-medium' : 'explosion-large', 0.7);
          this.score += h.score;
        } else {
          this.feel.hitSpark(h.x, h.y, 0.4);
        }
      }
    });
    if (result.kills > 0) {
      this.audio.say('rios-contact', { once: true });
      // Kill chain — every eighth kill, per the bark's trigger spec.
      if (Math.floor(this.kills / 8) > Math.floor((this.kills - result.kills) / 8)) {
        this.audio.say('rios-chain');
      }
    }

    // CROSSFIRE. Enemy fire damages enemies. The Lattice does not value its
    // own units, so a wave that boxes the player in also boxes itself in —
    // and dodging becomes an offensive act rather than a purely defensive one.
    const cross = this.enemies.resolveCrossfire(this.bullets, (x, y, killed) => {
      if (killed) {
        this.feel.kill(x, y, 'light');
        this.audio.play('drone-pop', 0.45, x / PLAYFIELD.halfWidth);
      } else {
        // Cool blue-white sparks so friendly-fire hits read differently from
        // the player's own hits at a glance.
        this.feel.hitSpark(x, y, 0.9);
      }
    });
    this.kills += cross.kills;
    this.score += cross.score;
    if (cross.kills > 0) this.hud.announceCrossfire(cross.kills, cross.score);

    const hitByBullet = this.bullets.collidePlayer(
      playerX,
      playerY,
      this.player.hitRadius,
    );
    const hitByMissile = this.enemyMissiles.collidePlayer(playerX, playerY, this.player.hitRadius);
    const hitByEnemy = this.enemies.collidePlayer(
      playerX,
      playerY,
      this.player.hitRadius,
    );
    // Flying into the Mother's body is flying into a drone. The mass is not a
    // wall with a hitbox — contact is resolved against the individual members,
    // so a corridor the player has cut is genuinely safe to fly down.
    const hitBySwarm =
      this.mother.active && this.mother.hits(playerX, playerY, this.player.hitRadius);
    // Shield absorbs one hit and breaks loudly, so the player knows the buffer
    // is gone rather than quietly losing it.
    if ((hitByBullet || hitByEnemy || hitByMissile || hitBySwarm) && this.player.shield > 0) {
      this.player.shield -= 1;
      this.bullets.clear();
      this.feel.addShake(0.4);
      this.feel.addHitstop(140);
      this.feel.kill(playerX, playerY, 'armored');
      this.audio.play('explosion-medium', 0.8);
      this.player.invulnerability = 1.1;
    } else if ((hitByBullet || hitByEnemy || hitByMissile || hitBySwarm) && this.player.takeHit()) {
      this.effects.spawnPlayerDamage(playerX, playerY);
      this.bullets.clear();
      this.feel.kill(playerX, playerY, 'structure');
      this.feel.addShake(0.8);
      this.audio.play('player-death', 1.0);
      this.audio.sayOneOf(
        ['rios-death', 'rios-death-2', 'rios-death-3', 'rios-death-4', 'rios-death-5'],
        { priority: 1 },
      );
      if (this.player.lives <= 0) this.failed = true;
    }

    if (this.frame % 2 === 0 && this.player.group.visible) {
      this.effects.spawnEngineExhaust(
        this.player.group.position.x,
        this.player.group.position.y,
        this.frame,
      );
    }
    this.effects.update(delta);
    this.feel.update(delta);
    if (this.stageDirector.isComplete(this.runElapsed)) this.complete = true;
    this.enemies.syncVisuals();
    this.playerShots.syncVisuals();
    this.bullets.syncVisuals();
    this.hud.update(this.hudState());
    this.publishDiagnostics();
  }

  /** Camera home position; shake is applied as an offset from here. */
  private readonly cameraHome = new THREE.Vector3(0, -37, 43);
  private readonly shakeVec = new THREE.Vector2();

  private render(alpha = 1): void {
    // Smooth the player between fixed steps so high-refresh displays get
    // motion at their native rate rather than 60 Hz judder.
    this.player.interpolate(alpha);
    // Screenshake is a render-time camera offset, never a simulation change —
    // the playfield and every hitbox stay exactly where they were.
    const s = this.feel.shakeOffset(this.shakeVec);
    this.camera.position.set(
      this.cameraHome.x + s.x,
      this.cameraHome.y + s.y,
      this.cameraHome.z,
    );
    this.renderer.info.reset();
    this.composer.render();
  }

  private createScene(): void {
    this.scene.background = new THREE.Color('#7d93a0');
    // FOG MUST START BEYOND THE PLAYFIELD. The camera sits at (0,-37,43), so
    // the play area spans roughly 47-78 units of view distance and the player
    // themself sits at ~58. The old near plane of 58 therefore began fogging
    // exactly at the ship and washed out everything the player was flying
    // toward — the single biggest reason the stage read as flat and grey.
    //
    // Starting at 82 leaves gameplay entirely clear and keeps the haze for the
    // far ground plane, where aerial perspective belongs.
    this.scene.fog = new THREE.Fog('#93a7ad', 82, 145);
    const hemisphere = new THREE.HemisphereLight('#d9e7e7', '#29301e', 1.15);
    this.scene.add(hemisphere);

    const sun = new THREE.DirectionalLight('#fff1c7', 1.9);
    sun.position.set(-10, 16, 24);
    sun.castShadow = true;
    // 2048 over a 44x56 shadow frustum is ~37 texels/unit, roughly double the
    // old density. Contact shadows on props were mushy enough to read as dirt.
    sun.shadow.mapSize.set(2048, 2048);
    sun.shadow.bias = -0.0006;
    sun.shadow.normalBias = 0.02;
    sun.shadow.camera.left = -22;
    sun.shadow.camera.right = 22;
    sun.shadow.camera.top = 28;
    sun.shadow.camera.bottom = -28;
    sun.shadow.camera.near = 1;
    sun.shadow.camera.far = 70;
    this.scene.add(sun);

    const rim = new THREE.DirectionalLight('#9cd8ff', 1.05);
    rim.position.set(12, -16, 30);
    this.scene.add(rim);

    this.scene.add(
      this.environment.group,
      this.player.group,
      this.enemies.group,
      this.bullets.group,
      this.playerShots.mesh,
      this.effects.group,
      this.feel.group,
      this.boss.group,
      this.props.group,
      this.pickups.group,
      this.midboss.group,
      this.homing.group,
      this.allies.group,
      this.enemyMissiles.group,
      this.mother.group,
    );
    // PRE-WARM SHADERS. Three compiles a material's program the first time it
    // is actually drawn, which lands as a multi-frame stall in the middle of
    // gameplay — the first explosion, the first boss, the first pickup. This
    // pays all of it up front while the title screen is showing.
    this.renderer.compile(this.scene, this.camera);
    void this.boss.load();
    void this.props.load();
    void this.pickups.load();
    void this.allies.load();
    void this.midboss.load();
  }

  private resetRun(): void {
    // Starting loadout comes from the selected tier.
    const diff = getDifficulty();
    this.runElapsed = 0;
    this.score = 0;
    this.kills = 0;
    this.medals = 0;
    this.midbossCleared = false;
    this.paused = false;
    this.extendsAwarded.clear();
    this.firstMissileDropped = false;
    this.scrollNow = this.tuning.scrollSpeed;
    this.bossBeamTimer = 0;
    this.failed = false;
    this.complete = false;
    this.player.reset();
    this.player.lives = diff.startLives;
    this.player.shield = diff.startShield;
    this.player.power = diff.startPower;
    this.bullets.reset();
    this.playerShots.reset();
    this.enemies.reset();
    this.effects.reset();
    this.feel.reset();
    this.boss.reset();
    this.props.reset();
    this.pickups.reset();
    this.midboss.reset();
    this.homing.reset();
    this.allies.reset();
    this.enemyMissiles.reset();
    this.environment.reset();
    this.stageDirector.reset();
    this.hud.update(this.hudState());
  }

  private setStressState(): void {
    this.resetRun();
    const phaseSeed = this.rng() * Math.PI * 2;
    for (let ring = 0; ring < 16; ring += 1) {
      this.bullets.spawnRing(
        (ring % 4) * 7 - 10.5,
        18 - Math.floor(ring / 4) * 5,
        24,
        3.8 + (ring % 3),
        phaseSeed + ring * 0.13,
      );
    }
    for (let index = 0; index < 36; index += 1) {
      this.enemies.spawn(
        index % 3 === 0 ? EnemyRole.Roundel : EnemyRole.Tick,
        -16 + (index % 12) * 2.9,
        23 - Math.floor(index / 12) * 4,
      );
    }
    this.enemies.syncVisuals();
    this.bullets.syncVisuals();
  }

  private hudState() {
    // Boss panel binds to whichever encounter is live. The midboss reports a
    // simplified two-part readout; BATTERY NINE reports all four components.
    let boss: {
      name: string;
      integrity: number;
      parts: { id: string; frac: number; alive: boolean }[];
    } | null = null;

    if (this.boss.active && !this.boss.defeated) {
      boss = {
        name: 'BATTERY NINE',
        integrity: this.boss.healthFraction,
        parts: this.boss.componentStatus(),
      };
    } else if (this.midboss.active && !this.midboss.defeated) {
      boss = {
        name: 'TWO VEHICLES',
        integrity: this.midboss.healthFraction,
        parts: this.midboss.componentStatus(),
      };
    }

    return {
      score: this.score,
      lives: this.player.lives,
      bombs: this.player.bombs,
      medals: this.medals,
      shield: this.player.shield,
      elapsed: this.runElapsed,
      progress: this.stageDirector.getProgress(this.runElapsed),
      objective: this.stageDirector.getLabel(),
      failed: this.failed,
      complete: this.complete,
      focused: this.input.isFocusHeld(),
      weapon: this.player.weapon,
      weaponEffect: this.weaponEffectiveness(),
      boss,
    };
  }

  /**
   * How well the loaded weapon performs against what is actually on screen,
   * as 0..1. Averaging across live targets means the bars answer the question
   * the player is really asking — "is this the right tool right now?" — rather
   * than quoting a static table.
   */
  private weaponEffectiveness(): number {
    const armor = this.enemies.dominantArmor();
    if (armor === null) return 0.6;
    const mult = ARMOR_RESPONSE[this.player.weapon][armor];
    return Math.max(0, Math.min(1, (mult - 0.55) / 0.85));
  }

  private installTestHooks(): void {
    window.__THREE_GAME_TEST_HOOKS__ = {
      seed: (value: number) => {
        this.rng = createSeededRandom(value);
      },
      setState: (name: string) => {
        this.title.dismiss();
        if (name === 'active-play') this.resetRun();
        else if (name === 'stress') this.setStressState();
        else if (name === 'midboss') {
          this.resetRun();
          this.runElapsed = 140;
          this.stageDirector.skipTo(140);
          this.enemies.reset();
          this.bullets.clear();
          this.midboss.spawn();
        }
        else if (name === 'roster') {
          // Dev-only: one of every enemy type, laid out for visual inspection.
          this.resetRun();
          this.stageDirector.skipTo(200);
          this.enemies.reset();
          this.bullets.clear();
          for (let r = 0; r < 10; r += 1) {
            const col = r % 5;
            const row = Math.floor(r / 5);
            this.enemies.spawn(r as never, -13 + col * 6.5, 14 - row * 9);
          }
          this.enemies.syncVisuals();
        }
        else if (name === 'boss') {
          // Jump straight to the BATTERY NINE gate. Used by QA and by the
          // demo build so the boss can be shown without a 170 s run-up.
          this.resetRun();
          this.runElapsed = 170;
          // Advance the director past every wave without running them,
          // otherwise 170 s of spawns land in one frame.
          this.stageDirector.skipTo(170);
          this.enemies.reset();
          this.bullets.clear();
          this.mother.spawn();
        }
        else if (name === 'fail') {
          this.resetRun();
          this.player.lives = 0;
          this.failed = true;
        } else if (name === 'complete') {
          this.resetRun();
          this.runElapsed = 122;
          this.complete = true;
        } else console.warn(`Unknown test state: ${name}`);
        this.hud.update(this.hudState());
      },
      setPausedForScreenshot: (paused: boolean) => {
        this.pausedForScreenshot = paused;
      },
      setReducedMotion: (enabled: boolean) => {
        this.reducedMotion = enabled;
      },
      /** Dev-only: bullet field for the automated playtest bot. */
      bulletField: () => {
        const buf = new Float32Array(6000);
        const n = this.bullets.snapshot(buf);
        return { n, data: Array.from(buf.subarray(0, n * 5)) };
      },
      /**
       * Dev-only: hand the live scene graph to a harness.
       *
       * Grepping source for a stray colour missed the same artifact three
       * times, because a material can be authored in a GLB, overridden at
       * load, or cloned at runtime — and only the last of those is visible in
       * the running scene. Inspect the thing that is actually drawing.
       */
      scene: () => this.scene,
      hideDebugUi: (hidden: boolean) => {
        this.debugTools.setHidden(hidden);
      },
      /**
       * Dev-only: fast-forward the scroll so a QA pass can photograph each
       * zone of the authored journey, without combat running.
       *
       * Stepped in COARSE 20-unit chunks, not at 1/60 s. Every terrain wrap
       * test is a single `if`, so a step only has to stay below the shortest
       * wrap distance in the system (the 68 u barn cycle) to stay correct —
       * 20 is comfortably inside that. Stepping at the real timestep instead
       * costs 4,200 iterations per zone, each one rewriting and re-uploading
       * every instance matrix in the prop field, which took the tour from
       * instant to several minutes.
       */
      /**
       * Dev-only: bring up the Swarm Mother alone, so the body can be verified
       * without a 170 s run-up. `formation` selects the shape directly.
       */
      swarm: (formation?: string) => {
        // dismiss() is REQUIRED. Without it the title screen stays up and the
        // simulation never steps, so the body renders but never updates: the
        // collision grid is never rebuilt and dead drones keep reporting as
        // solid. That produced a boss that looked right and was inert.
        this.title.dismiss();
        this.resetRun();
        this.enemies.reset();
        this.bullets.clear();
        // Body-only: the gate verifies the body's standalone guarantees, and
        // a live encounter reshaping and healing it would make them
        // unmeasurable. setState('boss') brings up the real fight.
        this.mother.spawn(true);
        // The harness photographs shapes over many minutes of wall-clock; a
        // live stage would kill the player and drop a death overlay over the
        // very formations being inspected. This is dev-only isolation.
        this.player.invulnerability = 1e9;
        if (formation) this.mother.body.setFormation(formation as never);
      },
      /** Change shape WITHOUT healing — damage must survive a morph. */
      swarmFormation: (formation: string) => {
        this.mother.body.setFormation(formation as never);
      },
      /** Dev-only: read the body's state for the hole-persistence check. */
      swarmState: () => ({
        living: this.mother.body.living,
        integrity: this.mother.body.integrity,
        morphing: this.mother.body.morphing,
        simTime: this.mother.body.simTime,
      }),
      /** Dev-only: cut into the body at a world point, as player fire would. */
      swarmCut: (x: number, y: number, radius: number) =>
        this.mother.body.damage(x, y, radius, 99),
      /** Dev-only: is there a clear corridor between two world points? */
      swarmClear: (x0: number, y0: number, x1: number, y1: number) =>
        this.mother.body.clearLineTo(x0, y0, x1, y1),
      tourTo: (units: number) => {
        this.resetRun();
        this.enemies.reset();
        this.bullets.clear();
        const speed = this.tuning.scrollSpeed;
        const CHUNK = 20;
        let travelled = 0;
        while (travelled < units) {
          const advance = Math.min(CHUNK, units - travelled);
          const dt = advance / speed;
          this.environment.update(dt, speed);
          this.props.update(dt, speed);
          travelled += advance;
        }
      },
    };
  }

  private publishDiagnostics(): void {
    const info = this.renderer.info;
    window.__THREE_GAME_DIAGNOSTICS__ = {
      frame: this.frame,
      elapsed: this.runElapsed,
      score: this.score,
      targetScore: 0,
      complete: this.complete,
      failed: this.failed,
      stage: 'HOME_FIELD',
      objective: this.stageDirector.getLabel(),
      entities: {
        enemies: this.enemies.count,
        enemyBullets: this.bullets.count,
        playerShots: this.playerShots.count,
      },
      player: {
        position: {
          x: this.player.group.position.x,
          y: this.player.group.position.y,
          z: this.player.group.position.z,
        },
        speed: this.player.velocity.length(),
        lives: this.player.lives,
        bombs: this.player.bombs,
      },
      renderer: {
        calls: info.render.calls,
        triangles: info.render.triangles,
        geometries: info.memory.geometries,
        textures: info.memory.textures,
      },
      canvas: {
        clientWidth: this.canvas.clientWidth,
        clientHeight: this.canvas.clientHeight,
        width: this.canvas.width,
        height: this.canvas.height,
        dpr: Math.min(window.devicePixelRatio || 1, this.tuning.maxDpr),
      },
    };
  }

  private resizeComposer(): void {
    this.composer.setPixelRatio(
      Math.min(window.devicePixelRatio || 1, this.tuning.maxDpr),
    );
    this.composer.setSize(this.canvas.clientWidth, this.canvas.clientHeight);
  }

  private getElement(selector: string): HTMLElement {
    const element = document.querySelector<HTMLElement>(selector);
    if (!element) throw new Error(`Missing element: ${selector}`);
    return element;
  }
}
